// Code that only some pages or visitors use, kept out of the pages that don't, without changing
// what the server renders or what hydration shows.
//
// Each entry of LAZY_MODULES is a chunk of its own. Render its components through `lazyComponent`:
//
// - A page that renders one with the document calls `preloadLazyModule(key)` from its route loader
//   (on the server and on client navigations, so rendering never waits). On the server that also
//   links the chunk and its imports as `<link rel="modulepreload">` in the document's <head>
//   (vite/lazyModuleAssets.ts maps keys to built chunks) and names the module in the router's
//   dehydrated state, and the browser loads every named module before it hydrates (src/router.tsx).
//   Hydration therefore renders exactly what the server sent; nothing waits behind a Suspense
//   boundary, so no later context update can make React discard server HTML.
// - A component that opens on an interaction (a dialog) is loaded then: `loadLazyModule(key)` on
//   hover or focus, and the component renders, suspending until the chunk arrives, once the visitor
//   opens it. The click is kept: the state change that opens it happens right away.
//
// Never render a lazy component with the document without preloading it in the route loader: the
// server would render it, but the browser would hydrate it only after its chunk loads (a warning is
// logged in development).
import { type ComponentType, createElement, Suspense, use } from "react";

/**
 * The lazily loaded modules, by key. vite/lazyModuleAssets.ts reads this object (`key: () =>
 * import("specifier")`, one per line) to find each key's chunks, so keep that form.
 */
export const LAZY_MODULES = {
  schemaLanguagePanel: () => import("src/components/docs/SchemaExport/SchemaLanguagePanel"),
  schemaExportDialog: () => import("src/components/docs/SchemaExport/SchemaExportDialog"),
  /** The signed-in visitor's topbar actions and project menu (AppLayout). */
  signedInShell: () => import("src/components/layout/AppLayout/SignedInShell"),
  /** The rename and invite dialogs of the topbar switchers' "More" menus, loaded on first use. */
  scopeActionDialogs: () => import("src/components/app/ScopeSwitchers/ScopeActionDialogs"),
  /** The portal's blocking "Accept the Terms" dialog, for people who haven't accepted the current Terms. */
  termsAcceptanceDialog: () =>
    import("src/components/app/TermsAcceptanceDialog/TermsAcceptanceDialog"),
  /** The portal's one-time "Choose a moderation policy" dialog for an automatic default project. */
  moderationPolicyChoiceDialog: () =>
    import(
      "src/routes/_authed/portal/-components/moderation-policies/ModerationPolicyChoiceDialog"
    ),
  /** The topbar's search dialog, loaded when it's first opened. */
  assetSearchDialog: () => import("src/components/app/AssetSearchDialog/AssetSearchDialog"),
  /** The usage page's charts (recharts). */
  usageChart: () => import("src/components/app/UsageDashboardPage/UsageChart"),
  /**
   * The docs Quick start's moderation playground (form, API client, results). Only visitors with a
   * selected project see it; everyone else gets a sign-in prompt (ModerationPlaygroundDocs).
   */
  moderationPlayground: () =>
    import("src/components/app/ModerationPlayground/ModerationPlayground"),
  /** The SDK widgets working above their code in the docs, loaded once an example nears view. */
  widgetPreviews: () => import("src/components/docs/WidgetPreview/WidgetPreviews"),
  /** react-toastify and the toast container, loaded on idle or by the first toast (Toasts.tsx). */
  toasts: () => import("src/components/common/Toasts/ToastRuntime"),
  adminDashboard: () => import("src/components/admin/AdminDashboard/AdminDashboard"),
  /** Back office tabs: each loads with its own `?tab=`. */
  adminCompliance: () => import("src/components/admin/AdminCompliance/AdminCompliance"),
  adminAnalytics: () => import("src/components/admin/AdminAnalytics/AdminAnalytics"),
  adminSupportTickets: () =>
    import("src/components/admin/AdminSupportTicketsList/AdminSupportTicketsList"),
  adminSafetyHolds: () => import("src/components/admin/AdminSafetyHoldsList/AdminSafetyHoldsList"),
  adminUsers: () => import("src/components/admin/AdminUsersList/AdminUsersList"),
  adminProjects: () => import("src/components/admin/AdminProjectsList/AdminProjectsList"),
  adminOrganisations: () =>
    import("src/components/admin/AdminOrganisationsList/AdminOrganisationsList"),
  adminWaitlist: () => import("src/components/admin/AdminWaitlistList/AdminWaitlistList"),
  adminSubprocessors: () =>
    import("src/components/admin/AdminSubprocessorNotices/AdminSubprocessorNotices"),
};

export type LazyModuleKey = keyof typeof LAZY_MODULES;
export type LazyModule<K extends LazyModuleKey> = Awaited<ReturnType<(typeof LAZY_MODULES)[K]>>;

/**
 * Each key's built chunk and its static imports (`/assets/…js`), minus the client entry's. Filled in
 * by vite/lazyModuleAssets.ts in the Worker build only; empty in `vite dev`, the browser and tests.
 */
const LAZY_MODULE_ASSETS: Partial<Record<LazyModuleKey, string[]>> = /* @lazy-module-assets */ {};

/**
 * A module's load. Once loaded it carries React's thenable fields (`status`, `value`), so `use()`
 * returns the module synchronously instead of suspending.
 */
type ModuleLoad<M> = Promise<M> & { status?: "fulfilled"; value?: M };
const loading = new Map<LazyModuleKey, ModuleLoad<unknown>>();

/** Whether the module has loaded. */
export const isLazyModuleLoaded = (key: LazyModuleKey): boolean =>
  loading.get(key)?.status === "fulfilled";

/** Loads the module (once; a failed load is retried by the next call). */
export const loadLazyModule = <K extends LazyModuleKey>(key: K): ModuleLoad<LazyModule<K>> => {
  let load = loading.get(key) as ModuleLoad<LazyModule<K>> | undefined;
  if (!load) {
    const started: ModuleLoad<LazyModule<K>> = (LAZY_MODULES[key]() as Promise<LazyModule<K>>).then(
      (module) => {
        started.status = "fulfilled";
        started.value = module;
        return module;
      },
      (error: unknown) => {
        loading.delete(key);
        throw error;
      },
    );
    load = started;
    loading.set(key, load);
  }
  return load;
};

/** Loads every module the server rendered the document with, before the browser hydrates it. */
export const loadLazyModules = async (keys: readonly string[] | undefined): Promise<void> => {
  // A chunk that fails to load must not stop hydration: its component then hydrates when (if) a
  // retry succeeds, keeping the server HTML until then.
  await Promise.allSettled(
    (keys ?? [])
      .filter((key) => Object.hasOwn(LAZY_MODULES, key))
      .map((key) => loadLazyModule(key as never)),
  );
};

// Server only: the modules this request's page renders with. TanStack Start keeps each request's
// context in an AsyncLocalStorage on `globalThis` (@tanstack/start-storage-context); its
// `requestAssets` are added to the document's preloads when the router dehydrates.
interface StartContext {
  requestAssets?: { preloads?: Array<string | { href: string }> };
}
const START_CONTEXT_KEY = Symbol.for("tanstack-start:start-storage-context");
const requestModules = new WeakMap<StartContext, Set<LazyModuleKey>>();

const getStartContext = (): StartContext | undefined => {
  if (typeof window !== "undefined") return undefined;
  const storage = (
    globalThis as { [START_CONTEXT_KEY]?: { getStore(): StartContext | undefined } }
  )[START_CONTEXT_KEY];
  return storage?.getStore();
};

const recordRequestModule = (key: LazyModuleKey) => {
  const context = getStartContext();
  if (!context) return;
  let modules = requestModules.get(context);
  if (!modules) {
    modules = new Set();
    requestModules.set(context, modules);
  }
  if (modules.has(key)) return;
  modules.add(key);
  const hrefs = LAZY_MODULE_ASSETS[key];
  if (!hrefs?.length) return;
  context.requestAssets ??= {};
  const preloads = context.requestAssets.preloads ?? [];
  const linked = new Set(
    preloads.map((preload) => (typeof preload === "string" ? preload : preload.href)),
  );
  context.requestAssets.preloads = [...preloads, ...hrefs.filter((href) => !linked.has(href))];
};

/**
 * Loads a module the page renders with, from its route loader. Awaiting it means the page renders
 * without suspending; on the server it also preloads the chunk and has the browser load it before
 * hydrating. Never rejects: if the chunk fails to load, the component retries while rendering and
 * an error reaches its error boundary instead of failing the whole route.
 */
export const preloadLazyModule = async (key: LazyModuleKey): Promise<void> => {
  recordRequestModule(key);
  await loadLazyModule(key).catch(() => {});
};

/** Server only: the modules this request's page renders with, for the router's dehydrated state. */
export const getRequestLazyModules = (): LazyModuleKey[] => {
  const context = getStartContext();
  return context ? [...(requestModules.get(context) ?? [])] : [];
};

/** Modules a render has suspended on (see `lazyComponent`). */
const suspendedModules = new Set<LazyModuleKey>();

type ExportProps<K extends LazyModuleKey, E extends keyof LazyModule<K>> =
  LazyModule<K>[E] extends ComponentType<infer P> ? P : never;

/**
 * A component exported by a lazy module. It renders synchronously once the module has loaded
 * (always, for pages that preload it) and otherwise suspends inside its own boundary, rendering
 * nothing until the chunk arrives.
 */
export const lazyComponent = <K extends LazyModuleKey, E extends keyof LazyModule<K>>(
  key: K,
  exportName: E,
): ComponentType<ExportProps<K, E>> => {
  const Loaded = (props: object) => {
    if (
      process.env.NODE_ENV === "development" &&
      typeof window === "undefined" &&
      !isLazyModuleLoaded(key)
    ) {
      console.warn(
        `Lazy module "${key}" rendered on the server without preloadLazyModule("${key}") in a route loader: the browser will hydrate it late.`,
      );
    }
    const load = loadLazyModule(key);
    // A loaded module renders without `use()` (which, among other things, makes React's `act()`
    // treat the render as asynchronous). Once a render has had to suspend on the module, keep
    // calling `use()` for it, as React requires; its load is fulfilled then, so it doesn't suspend.
    let module: LazyModule<K>;
    if (load.status === "fulfilled" && !suspendedModules.has(key)) {
      module = load.value as LazyModule<K>;
    } else {
      suspendedModules.add(key);
      module = use(load as Promise<LazyModule<K>>);
    }
    return createElement(module[exportName] as ComponentType<object>, props);
  };
  Loaded.displayName = `Lazy(${key}.${String(exportName)})`;
  const Lazy = (props: ExportProps<K, E>) => (
    <Suspense fallback={null}>
      <Loaded {...(props as object)} />
    </Suspense>
  );
  Lazy.displayName = `LazyBoundary(${key}.${String(exportName)})`;
  return Lazy;
};
